import { NpcAvatar } from '@/components/admin/npc/npc-card';
import { useRowDrag } from '@/components/admin/npc/use-row-drag';
import { IconButton } from '@/components/admin/reglages/reference-list';
import {
  type BenchDraft,
  benchDraftOf,
  benchWriteOf,
  canAddMembers,
  canSaveBench,
  memberOfCard,
  pickableCards,
  withMemberAt,
  withMemberMoved,
  withMemberRemoved,
  withMembersAdded,
} from '@/lib/npc-bench-draft';
import { NPC_KIND_LABELS } from '@matter_extractor/shared';
import type { NpcBench, NpcCard } from '@matter_extractor/shared';
import { NPC_BENCH_NAME_MAX } from '@matter_extractor/shared';
import { Alert, Checkbox, Icon, TextField, Tooltip } from '@pms-digital/design-system';
import { useState } from 'react';

/**
 * L'éditeur d'un banc (npc.md §5, H4; maquette `Bibliotheque`, planche « c. L'onglet Bancs »).
 *
 * A name and an ORDERED list of NPC - which is all a bench is. It is not versioned, so the editor is
 * a small form written WHOLE by one button, with no autosave and no history, and says so under the
 * list: a series freezes the list and the version of each NPC at its launch, so touching a bench
 * changes no series already played.
 *
 * The list moves by the grip's native drag and by two arrows that name what they move; « Ajouter des
 * NPC » opens a picker of the live NPC not already there - retired ones are not offered (H24) but
 * stay where they are when the bench already carries them, flagged « retiré », because the launch
 * skips them and says so.
 *
 * The draft is state seeded from the props and never by an effect: the page mounts this with a `key`
 * on the bench, so « the bench just written » is a REMOUNT on the base - the régime of every admin
 * screen that writes.
 *
 * `cards` is the library, for the picker; `undefined` while it is read, and the picker then says so.
 */

export interface NpcBenchEditorProps {
  /** The bench, or null for a new one. */
  bench: NpcBench | null;
  cards: readonly NpcCard[] | undefined;
  busy: boolean;
  /** The refusal of the last write, already worded. */
  error: string | null;
  onSave: (write: { name: string; npcIds: string[] }) => void;
  onRetire: () => void;
}

export function NpcBenchEditor({
  bench,
  cards,
  busy,
  error,
  onSave,
  onRetire,
}: NpcBenchEditorProps) {
  const [draft, setDraft] = useState<BenchDraft>(() => benchDraftOf(bench));
  const [picking, setPicking] = useState(false);
  const retired = bench?.retiredAt != null;
  const drag = useRowDrag(
    draft.members.map((member) => member.id),
    (id, index) => setDraft(withMemberAt(draft, id, index)),
  );
  const write = benchWriteOf(draft);

  return (
    <section data-testid="npc-banc-editeur" className="space-y-5">
      <TextField
        label="Nom du banc"
        data-testid="npc-banc-nom"
        value={draft.name}
        readOnly={retired}
        required
        aria-required="true"
        maxLength={NPC_BENCH_NAME_MAX}
        onChange={(event) => setDraft({ ...draft, name: event.currentTarget.value })}
        help="Unique parmi les bancs."
      />

      <div className="space-y-2">
        <h3 className="font-semibold text-gray-800 text-small">
          Ses NPC, dans l’ordre{' '}
          <span className="font-normal text-gray-600">({draft.members.length})</span>
        </h3>
        {draft.members.length === 0 ? (
          <p data-testid="npc-banc-vide" className="text-gray-600 text-small italic">
            Aucun NPC : un banc vide ne se lance pas.
          </p>
        ) : (
          <ol className="divide-y divide-gray-200 rounded-md border border-gray-200 bg-white">
            {draft.members.map((member, index) => {
              const hint = drag.hint?.key === member.id ? drag.hint : null;
              return (
                <li
                  key={member.id}
                  data-testid="npc-banc-membre"
                  {...drag.row(member.id)}
                  className={`flex items-center gap-2 px-2 py-1.5 ${
                    hint === null
                      ? ''
                      : `border-bleu-nuit ${hint.after ? 'border-b-4' : 'border-t-4'}`
                  } ${drag.dragging === member.id ? 'opacity-40' : ''}`}
                >
                  {retired ? null : (
                    <Tooltip content="Glisser pour réordonner">
                      <span
                        {...drag.grip(member.id)}
                        data-testid="npc-banc-poignee"
                        className="flex size-8 shrink-0 cursor-grab items-center justify-center rounded-md text-gray-400"
                      >
                        <Icon name="drag_indicator" size={16} />
                        <span className="sr-only">NPC {index + 1}</span>
                      </span>
                    </Tooltip>
                  )}
                  <span aria-hidden="true" className="w-5 shrink-0 text-caption text-gray-500">
                    {index + 1}
                  </span>
                  <NpcAvatar firstName={member.firstName} kind={member.kind} size="sm" />
                  <span className="min-w-0 flex-1 truncate text-gray-900 text-small">
                    {member.name}
                    {member.retired ? (
                      <span data-testid="npc-banc-membre-retire" className="text-gray-600">
                        {' '}
                        · retiré, sauté au lancement
                      </span>
                    ) : null}
                  </span>
                  <span className="sr-only">{NPC_KIND_LABELS[member.kind]}</span>
                  {retired ? null : (
                    <span className="flex shrink-0 items-center gap-1">
                      <IconButton
                        label={`Monter ${member.name}`}
                        disabled={index === 0}
                        testId="npc-banc-monter"
                        onClick={() => setDraft(withMemberMoved(draft, member.id, -1))}
                      >
                        <Icon name="arrow_upward" size={16} />
                      </IconButton>
                      <IconButton
                        label={`Descendre ${member.name}`}
                        disabled={index === draft.members.length - 1}
                        testId="npc-banc-descendre"
                        onClick={() => setDraft(withMemberMoved(draft, member.id, 1))}
                      >
                        <Icon name="arrow_downward" size={16} />
                      </IconButton>
                      <IconButton
                        label={`Retirer ${member.name} du banc`}
                        tone="danger"
                        testId="npc-banc-sortir"
                        onClick={() => setDraft(withMemberRemoved(draft, member.id))}
                      >
                        <Icon name="close" size={16} />
                      </IconButton>
                    </span>
                  )}
                </li>
              );
            })}
          </ol>
        )}

        {retired ? null : picking ? (
          <Picker
            cards={cards}
            draft={draft}
            onAdd={(added) => {
              setDraft(withMembersAdded(draft, added));
              setPicking(false);
            }}
            onClose={() => setPicking(false)}
          />
        ) : (
          <button
            type="button"
            data-testid="npc-banc-ajouter"
            disabled={!canAddMembers(draft)}
            onClick={() => setPicking(true)}
            className="inline-flex h-10 cursor-pointer items-center gap-2 rounded-full border-2 border-gray-300 border-dashed bg-white px-4 font-semibold text-bleu-nuit text-small hover:bg-gray-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-bleu-nuit disabled:cursor-not-allowed disabled:text-gray-500"
          >
            <Icon name="add" size={16} />
            Ajouter des NPC
          </button>
        )}
      </div>

      {error === null ? null : (
        <div data-testid="npc-banc-refus">
          <Alert tone="danger">{error}</Alert>
        </div>
      )}

      <div className="flex flex-wrap items-center justify-between gap-4">
        {bench === null || retired ? (
          <span />
        ) : (
          <button
            type="button"
            data-testid="npc-banc-retirer"
            disabled={busy}
            onClick={onRetire}
            className="cursor-pointer font-semibold text-rouge-deep text-small hover:text-rouge disabled:cursor-not-allowed disabled:text-gray-500"
          >
            Retirer ce banc
          </button>
        )}
        {retired ? null : (
          <button
            type="button"
            data-testid="npc-banc-enregistrer"
            disabled={busy || write === null || !canSaveBench(draft, bench)}
            onClick={() => {
              if (write !== null) onSave(write);
            }}
            className="inline-flex h-11 cursor-pointer items-center rounded-md bg-bleu-nuit px-5 font-semibold text-body text-white hover:bg-bleu-nuit-deep focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-bleu-nuit focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:bg-gray-200 disabled:text-gray-500"
          >
            Enregistrer
          </button>
        )}
      </div>

      <p
        data-testid="npc-banc-note"
        className="border-gray-300 border-t border-dashed pt-3 text-caption text-gray-600 leading-4"
      >
        Un banc n’a pas de versions. Une série garde la liste avec laquelle elle a été lancée, et la
        version de chaque NPC à ce moment-là : retoucher ce banc ne change aucune série passée.
      </p>
    </section>
  );
}

interface PickerProps {
  cards: readonly NpcCard[] | undefined;
  draft: BenchDraft;
  onAdd: (members: ReturnType<typeof memberOfCard>[]) => void;
  onClose: () => void;
}

/** « Ajouter des NPC »: the live NPC not already in the bench, ticked then added in one go. */
function Picker({ cards, draft, onAdd, onClose }: PickerProps) {
  const [ticked, setTicked] = useState<readonly string[]>([]);
  const options = cards === undefined ? [] : pickableCards(cards, draft);
  const added = options.filter((card) => ticked.includes(card.id)).map(memberOfCard);

  return (
    <div
      data-testid="npc-banc-choix"
      className="space-y-2 rounded-md border-2 border-gray-200 bg-gray-50 p-3"
    >
      <p className="font-semibold text-gray-800 text-small">Choisir des NPC</p>
      {cards === undefined ? (
        <p className="text-gray-500 text-small italic">Lecture des NPC…</p>
      ) : options.length === 0 ? (
        <p data-testid="npc-banc-choix-vide" className="text-gray-600 text-small">
          Aucun autre NPC à ajouter.
        </p>
      ) : (
        <ul className="max-h-64 space-y-1 overflow-y-auto">
          {options.map((card) => (
            <li key={card.id}>
              <div className="rounded-sm px-1.5 py-1 hover:bg-white">
                <Checkbox
                  data-testid="npc-banc-choix-npc"
                  checked={ticked.includes(card.id)}
                  onCheckedChange={(checked) =>
                    setTicked(
                      checked ? [...ticked, card.id] : ticked.filter((one) => one !== card.id),
                    )
                  }
                  label={
                    <span className="inline-flex min-w-0 items-center gap-2">
                      <NpcAvatar firstName={card.firstName} kind={card.kind} size="sm" />
                      <span className="min-w-0 truncate">{card.name}</span>
                    </span>
                  }
                />
              </div>
            </li>
          ))}
        </ul>
      )}
      <div className="flex justify-end gap-2">
        <button
          type="button"
          onClick={onClose}
          className="h-9 cursor-pointer rounded-md px-3 font-semibold text-bleu-nuit text-small hover:bg-white"
        >
          Annuler
        </button>
        <button
          type="button"
          data-testid="npc-banc-choix-valider"
          disabled={added.length === 0}
          onClick={() => onAdd(added)}
          className="h-9 cursor-pointer rounded-md bg-bleu-nuit px-3 font-semibold text-small text-white disabled:cursor-not-allowed disabled:bg-gray-200 disabled:text-gray-500"
        >
          {added.length === 0 ? 'Ajouter' : `Ajouter ${added.length}`}
        </button>
      </div>
    </div>
  );
}
